import { useEffect, useRef, useState } from "react";

const TOOLTIP_SELECTOR = [
  "[data-tooltip]",
  "[aria-label]",
  "[title]",
  "button",
  "a[href]",
  "input",
  "textarea",
  "select",
  "summary",
  "label",
  "[role='button']",
  "[role='tab']",
  "[role='menuitem']",
  "[role='option']",
  "[role='switch']",
  "[role='checkbox']",
  "[tabindex]:not([tabindex='-1'])",
  ".cf-sidebar-row",
  ".cf-topbar-pill",
  ".cf-badge-hover",
].join(",");

const clean = value => String(value || "").replace(/\s+/g, " ").trim();

function clipped(value, max = 180) {
  const text = clean(value);
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "..." : text;
}

function knownDescription(label, el) {
  const text = clean(label);
  const l = text.toLowerCase();
  const isChatRail = Boolean(el.closest(".cf-chat-rail-panel"));

  if (!text) return "";
  if (l.includes("zero retention")) return "Shows privacy, plan, and server status for this session.";
  if (l.startsWith("model:")) return `Open the model selector. Current ${text.replace(/\s+/g, " ")}.`;
  if (l.includes("new chat")) return "Start a new chat.";
  if (l.includes("search chats")) return "Search saved chats by title or message text.";
  if (l.includes("all chats")) return "Show every chat, regardless of folder.";
  if (isChatRail && l === "general") return "Show chats in the General folder.";
  if (l.includes("chat your conversations")) return "Open Chat and your conversation list.";
  if (l.includes("projects shared docs")) return "Open Projects for shared documents and project chats.";
  if (l.includes("more agents")) return "Expand additional tools, agents, and workflows.";
  if (l.includes("settings app preferences")) return "Open app preferences and configuration.";
  if (l.includes("admin settings")) return "Open admin-only settings for providers, users, and billing.";
  if (l.includes("install app")) return "Install ChatFortAI as an app on this device.";
  if (l.includes("sign out")) return "Sign out of this local session.";
  if (l.includes("toggle theme")) return "Switch between light and dark mode.";
  if (l.includes("what's new")) return "Open recent ChatFortAI updates.";
  if (l.includes("collapse sidebar")) return "Collapse the left navigation.";
  if (l.includes("expand sidebar")) return "Expand the left navigation.";
  if (l.includes("clear search") || l === "clear") return "Clear the current search text.";
  if (l === "search") return "Use web search with this prompt.";
  if (l === "chatfortai") return "ChatFortAI private AI assistant.";
  if (l === "send" || l === "send message") return "Send the current message.";
  return "";
}

function readableElementText(el) {
  const aria = clean(el.getAttribute("aria-label"));
  if (aria) return aria;
  const title = clean(el.getAttribute("title") || el.closest("[title]")?.getAttribute("title"));
  if (title) return title;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    return clean(el.placeholder || el.getAttribute("name") || el.type);
  }
  if (el instanceof HTMLSelectElement) {
    return clean(el.getAttribute("aria-label") || el.name || el.selectedOptions?.[0]?.textContent || "Choose an option");
  }
  return clean(el.innerText || el.textContent);
}

function describeTarget(el) {
  if (el.getAttribute("data-tooltip") === "false") return "";
  const explicit = clean(el.getAttribute("data-tooltip"));
  if (explicit) return clipped(explicit, 360);

  const label = readableElementText(el);
  const known = knownDescription(label, el);
  if (known) return clipped(known);

  const tag = el.tagName.toLowerCase();
  const role = clean(el.getAttribute("role"));
  const lowerLabel = label.toLowerCase();

  if (tag === "textarea") return clipped(label ? `Type here: ${label}.` : "Type your message or instructions here.");
  if (tag === "input") {
    if (el.type === "search" || lowerLabel.includes("search")) return clipped(label ? `Search: ${label}.` : "Search or filter this list.");
    return clipped(label ? `Field: ${label}.` : "Enter a value in this field.");
  }
  if (tag === "select") return clipped(label ? `Choose: ${label}.` : "Choose an option.");
  if (tag === "a") return clipped(label ? `Open: ${label}.` : "Open this link.");
  if (tag === "summary") return clipped(label ? `Expand or collapse: ${label}.` : "Expand or collapse this section.");
  if (tag === "button" || role === "button") {
    if (!label || label === "+" || label === "⊕") return "Use this control to add or create something here.";
    if (label === "×") return "Close or remove this item.";
    return clipped(`Use this control: ${label}.`);
  }
  if (role) return clipped(label ? `${role}: ${label}.` : `Interactive ${role}.`);
  return clipped(label);
}

function targetFrom(node) {
  if (!(node instanceof Element)) return null;
  let el = node.closest(TOOLTIP_SELECTOR);
  if (!el) {
    let cur = node;
    while (cur instanceof Element && cur !== document.body) {
      const style = window.getComputedStyle(cur);
      if (style.cursor === "pointer") {
        el = cur;
        break;
      }
      cur = cur.parentElement;
    }
  }
  if (!el || el.closest(".cf-global-tooltip,[data-tooltip-disabled='true']")) return null;
  const rect = el.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return null;
  return el;
}

function positionFor(el, pointer) {
  // Race guard: another handler (hide / blur / scroll) may have
  // dropped the active target between the calling event and this
  // setter running. Return null so callers can preserve prior state
  // instead of throwing.
  if (!el || typeof el.getBoundingClientRect !== "function") return null;
  const rect = el.getBoundingClientRect();
  // Detached node (removed from DOM) -- has 0,0,0,0 box. Treat as
  // "nothing to position relative to" and bail.
  if (rect.width === 0 && rect.height === 0 && rect.top === 0 && rect.left === 0) return null;
  const x = pointer?.clientX ?? rect.left + rect.width / 2;
  const top = pointer?.clientY ? pointer.clientY - 14 : rect.top - 10;
  const below = top < 58;
  return {
    x: Math.min(window.innerWidth - 18, Math.max(18, x)),
    y: below ? rect.bottom + 10 : top,
    placement: below ? "below" : "above",
  };
}

export default function GlobalTooltip({ theme }) {
  const [tip, setTip] = useState(null);
  const activeRef = useRef(null);
  const restoreTitleRef = useRef(null);
  const showTimerRef = useRef(null);

  useEffect(() => {
    const clearTimer = () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    };
    const restoreTitle = () => {
      const restored = restoreTitleRef.current;
      if (restored?.el && restored.title) restored.el.setAttribute("title", restored.title);
      restoreTitleRef.current = null;
    };
    const hide = () => {
      clearTimer();
      activeRef.current = null;
      restoreTitle();
      setTip(null);
    };
    const showFor = (el, pointer, immediate = false) => {
      const text = describeTarget(el);
      if (!text) {
        hide();
        return;
      }
      if (activeRef.current !== el) {
        restoreTitle();
        const titled = el.hasAttribute("title") ? el : el.closest("[title]");
        const title = titled?.getAttribute("title");
        if (titled && title) {
          restoreTitleRef.current = { el: titled, title };
          titled.removeAttribute("title");
        }
      }
      activeRef.current = el;
      const next = () => {
        const pos = positionFor(el, pointer);
        if (!pos) return; // detached / null target -- keep previous tip
        setTip({ text, ...pos });
      };
      clearTimer();
      if (immediate) next();
      else showTimerRef.current = setTimeout(next, 260);
    };
    const onPointerOver = event => {
      if (event.pointerType === "touch") return;
      const el = targetFrom(event.target);
      if (!el) return hide();
      showFor(el, event, false);
    };
    const onPointerMove = event => {
      if (event.pointerType === "touch" || !activeRef.current) return;
      setTip(prev => {
        if (!prev) return prev;
        // Re-check activeRef.current inside the updater -- it may
        // have been cleared (hide()) between the event fire and this
        // updater running.
        const target = activeRef.current;
        if (!target) return prev;
        const pos = positionFor(target, event);
        if (!pos) return prev;
        return { ...prev, ...pos };
      });
    };
    const onPointerOut = event => {
      if (!activeRef.current) return;
      const next = event.relatedTarget;
      if (next instanceof Node && activeRef.current.contains(next)) return;
      hide();
    };
    const onFocusIn = event => {
      const el = targetFrom(event.target);
      if (el) showFor(el, null, true);
    };
    const onFocusOut = () => hide();
    const onKeyDown = event => { if (event.key === "Escape") hide(); };
    const onScroll = () => hide();

    document.addEventListener("pointerover", onPointerOver, true);
    document.addEventListener("pointermove", onPointerMove, true);
    document.addEventListener("pointerout", onPointerOut, true);
    document.addEventListener("focus", onFocusIn, true);
    document.addEventListener("blur", onFocusOut, true);
    document.addEventListener("focusin", onFocusIn, true);
    document.addEventListener("focusout", onFocusOut, true);
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("pointerover", onPointerOver, true);
      document.removeEventListener("pointermove", onPointerMove, true);
      document.removeEventListener("pointerout", onPointerOut, true);
      document.removeEventListener("focus", onFocusIn, true);
      document.removeEventListener("blur", onFocusOut, true);
      document.removeEventListener("focusin", onFocusIn, true);
      document.removeEventListener("focusout", onFocusOut, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", hide);
      hide();
    };
  }, []);

  if (!tip) return null;

  const t = theme || {};
  const above = tip.placement === "above";
  return (
    <div
      className="cf-global-tooltip"
      role="tooltip"
      style={{
        position: "fixed",
        left: tip.x,
        top: tip.y,
        transform: above ? "translate(-50%, -100%)" : "translate(-50%, 0)",
        zIndex: 5000,
        maxWidth: 280,
        padding: "8px 10px",
        borderRadius: 8,
        background: t.toastBg || "rgba(15,23,42,0.94)",
        border: `1px solid ${t.border2 || "rgba(148,163,184,0.28)"}`,
        color: "#fff",
        fontSize: 11,
        lineHeight: 1.35,
        boxShadow: "0 12px 36px rgba(0,0,0,0.28)",
        pointerEvents: "none",
        whiteSpace: "normal",
        textAlign: "left",
      }}
    >
      {tip.text}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "50%",
          [above ? "bottom" : "top"]: -5,
          width: 9,
          height: 9,
          background: t.toastBg || "rgba(15,23,42,0.94)",
          borderRight: `1px solid ${t.border2 || "rgba(148,163,184,0.28)"}`,
          borderBottom: `1px solid ${t.border2 || "rgba(148,163,184,0.28)"}`,
          transform: above ? "translateX(-50%) rotate(45deg)" : "translateX(-50%) rotate(225deg)",
        }}
      />
    </div>
  );
}
